import { Skeleton, Text, TextColor, TextSize, Tooltip, TooltipTrigger } from '@vizit/atoms';
import { AlertCircleIcon } from '@vizit/icons';
import { cx } from '@vizit/theme';
import { plural } from '@vizit/utils';
import type * as React from 'react';
import { Button as AriaButton } from 'react-aria-components';

export interface BenchmarkDetailsHeaderProps {
  /**
   * The benchmark name
   */
  name: string;
  /**
   * The benchmark description, shown under the name when set
   *
   * @optional
   */
  description?: string | null;
  /**
   * Images the benchmark still needs before it can score; above zero, a warning by the name says so in a tooltip
   *
   * @optional
   * @default 0
   */
  imagesNeeded?: number;
  /**
   * Whether the benchmark is still loading
   *
   * @optional
   * @default false
   */
  isLoading?: boolean;
  /**
   * The filter bar under the name, e.g. the sort row and the benchmark summary bar
   *
   * @optional
   */
  children?: React.ReactNode;
  /**
   * Additional CSS classes for the root element
   *
   * @optional
   */
  className?: string;
}

/**
 * The warning by the name of a benchmark under its minimum: a focusable icon whose tooltip, on hover or keyboard
 * focus, says how many images are missing. The tooltip also becomes the button's description, so the name stays
 * short rather than repeating it.
 */
function ImagesNeededWarning({ imagesNeeded }: { imagesNeeded: number }) {
  const reason = `${plural(imagesNeeded, 'more image')} needed`;
  return (
    <TooltipTrigger delay={200}>
      <AriaButton
        aria-label="Not enough images"
        // Centred on the title's letters rather than its line box: the serif's line box sits a few pixels above them.
        className="flex shrink-0 translate-y-[3px] cursor-default items-center text-warning-600 outline-none data-[focus-visible]:ring-1 data-[focus-visible]:ring-gray-utility-700"
      >
        <AlertCircleIcon size={20} aria-hidden />
      </AriaButton>
      <Tooltip>{reason}</Tooltip>
    </TooltipTrigger>
  );
}

/**
 * The top of a benchmark page: its name, with a warning while the set is under its minimum, and description, then
 * the filter bar passed as `children`. Shows a loading placeholder for all of it until the benchmark loads.
 */
export function BenchmarkDetailsHeader(props: BenchmarkDetailsHeaderProps) {
  const { name, description, imagesNeeded = 0, isLoading = false, children, className } = props;

  if (isLoading) {
    return (
      <output className={cx('flex flex-col gap-6', className)} aria-label="Loading benchmark">
        <Skeleton variant="square" className="h-16 w-1/3" />
        <Skeleton variant="square" className="h-10 w-1/2" />
        <Skeleton variant="square" className="h-16 w-full" />
      </output>
    );
  }

  return (
    <div className={cx('flex flex-col gap-16', className)}>
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-4">
          <Text
            elementType="h1"
            color={TextColor.Primary}
            className="font-serif font-light text-display-sm"
            showEllipsis={false}
          >
            {name}
          </Text>
          {imagesNeeded > 0 && <ImagesNeededWarning imagesNeeded={imagesNeeded} />}
        </div>
        {description && (
          <Text size={TextSize.Sm} color={TextColor.Tertiary} showEllipsis={false}>
            {description}
          </Text>
        )}
      </div>
      {children && <div className="flex flex-col gap-6">{children}</div>}
    </div>
  );
}
